import React from "react";
import { Button } from "react-bootstrap";

import { useSearchParams } from "react-router-dom";
import { useAppDispatch } from "../../core/redux/hooks";

import VideoRoom from "./VideoRoom";
import ZoomVideoRoom from "./ZoomVideoRoom";
import AppModal from "../AppModal/AppModal";
import AppLoader from "../AppLoader/AppLoader";

import { GetApiMethod, PostApiMethod } from "../../core/services/apiService";
import { removeNotification, DisplayErrorMessages, GetTenantId, PhoneNumberFormatter } from "../../core/services/sharedService";

import { setHideNavBar } from "../../core/redux/authSlice";
import { showToaster } from "../../core/redux/toastSlice";
// asset files
import "./QrRoom.css";
import { decode } from "base-64";
import { checkAbilityToUseZOOM } from "../../core/services/sharedService";


const QrRoom = () => {

    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const locationId = searchParams.get("locationId") ? decode(searchParams.get("locationId")) : undefined;
    const typeOfCall = searchParams.get("type") ? searchParams.get("type") : "qrScan"
    const [roomName, setRoomName] = React.useState<string>("");
    const [tenantLogo, setTenantLogo] = React.useState<string | undefined>("");
    const [showPopUp, setShowPopUp] = React.useState<boolean>(false);
    const [callRefresh, setCallRefresh] = React.useState<number>(0);
    const [showLoader, setShowLoader] = React.useState<boolean>(false);
    const [serverValues, setServerValues] = React.useState<{ qrRoomToken: string, roomSid: string, sessionName: string, userName: string } | undefined>(undefined);
    const terminateVideoCallRef = React.useRef<Function>(null);
    const [callWaitingNumber, setCallWaitingNumber] = React.useState(0);
    const [remoteParticipantConnected, setRemoteParticipantConnected] = React.useState(false);
    const [checkCallQueueInterval, setCheckCallQueueInterval] = React.useState<any>(0);
    const [onlineAgentsCount, setOnlineAgentsCount] = React.useState<number>(0);
    const [customerContact, setCustomerContact] = React.useState<string>("");
    const [isHoliday, setIsHoliday] = React.useState<boolean>(false);
    const [currentDayWorkHrs, setCurrentWorkHrs] = React.useState<string>("");
    const [mediaPermissions, setMediaPermissions] = React.useState<boolean>(true);


    const toasterHandler = React.useCallback((dialogBgColor: string, dialogMsg: string) => {
        dispatch(showToaster({ dialogBgColor: dialogBgColor, dialogMsg: dialogMsg, showDialog: true, timer: '5000' }));
    }, [dispatch]);

    const tenantID = GetTenantId();

    // Initial render
    React.useEffect(() => {
        const localLocationId = localStorage.getItem("locationId");
        const localQrToken = localStorage.getItem("qrRoomToken");
        const localRoomSid = localStorage.getItem("roomSid");
        getAgentData();
        getLocationLogo();
        getWorkHrs();
        if (localLocationId && locationId && localLocationId === locationId && localQrToken && localRoomSid) {
            getCallStatus({ roomSid: localRoomSid, locationId: locationId, initialCheck: true });
        } else if (locationId) {
            dispatch(setHideNavBar(true));
            setRoomName(locationId ? locationId + "_" + Date.now() : "");
            localStorage.setItem("locationId", locationId);
        } else {
            toasterHandler("bg-danger", "Invalid request");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, locationId, callRefresh]);

    React.useEffect(() => {
        getUserMediaDetails();
    }, []);

    const getTodayDay = React.useCallback(() => {
        let intDayNumber = new Date().getDay();
        return intDayNumber === 0 ? "sunday" :
            intDayNumber === 1 ? "monday" :
                intDayNumber === 2 ? "tuesday" :
                    intDayNumber === 3 ? "wednesday" :
                        intDayNumber === 4 ? "thursday" :
                            intDayNumber === 5 ? "friday" : "saturday"
    }, []);

    const [inBreakHrs, setInBreakHrs] = React.useState<boolean>();

    const checkCurrentTimeWithinWorkHrs = (from: any, to: any, timeZone: string | null) => {
        let timestamp;
        if (timeZone) {
            const options = {
                timeZone: timeZone
            };
            const dateString = new Date().toLocaleString("en-US", options);
            const dateInTimeZone = new Date(dateString);
            timestamp = dateInTimeZone.getTime();
        } else {
            timestamp = new Date().getTime();
        }
        if ((timestamp > from && timestamp < to)) {
            // This means current time is in between work hours so the join call button need to be enabled
            setInBreakHrs(false);
            return true;
        } else {
            // This means current time is not in between work hours so join call button need to be disabled 
            setInBreakHrs(true);
            return false;
        }
    }

    let convert12hrto24hrFormat = (data: any) => {
        let [hours, minutes] = data.split(':');
        if (hours === '12') {
            hours = '00';
        }
        if (minutes.endsWith("PM")) {
            hours = parseInt(hours, 10) + 12;
        }
        return `${hours}:${minutes}`;
    }

    let hourNdMinutes = (time: any) => {
        let splitTime = time.split(":");
        // Adding the time ranges to the current Date.
        return new Date().setHours(splitTime[0], splitTime[1].slice(0, -2), 0, 0);
    }

    const getIndividualDates = React.useCallback((data: any, timeZone: string | null) => {
        let indVidualTimes = data.split("-");
        let from = convert12hrto24hrFormat(indVidualTimes[0]);
        let to = convert12hrto24hrFormat(indVidualTimes[1]);
        return checkCurrentTimeWithinWorkHrs(hourNdMinutes(from), hourNdMinutes(to), timeZone);
    }, []);

    let checkForEveryMinute = React.useCallback((data: any, timeZone: string | null) => {
        if (data.holiday) {
            setIsHoliday(true);
        } else {
            let diffSlots = data.time?.trim().split(",");
            let result = diffSlots.some((element: string) => (
                getIndividualDates(element, timeZone)
            ));
            setInBreakHrs(!result);
        }

    }, [getIndividualDates]);

    const getDateTimeFormat = React.useCallback((data: any, timeZone: string) => {
        checkForEveryMinute(data, timeZone);
        let interval: any = 0;
        interval = setInterval(() => {
            checkForEveryMinute(data, timeZone);
        }, 60000);

        return () => {
            clearInterval(interval);
        };
    }, [checkForEveryMinute]);

    const getWorkHrs = React.useCallback(() => {
        PostApiMethod('tenant/callHandOff/getWorkHrs', { locationId: locationId, day: getTodayDay() }).then((res: any) => {
            setCustomerContact(res.data?.data?.customer_contact);
            getDateTimeFormat(res.data.data.activeHrs, res.data?.data?.timeZone);
            setIsHoliday(res.data.data.activeHrs.holiday);
            setCurrentWorkHrs(res.data.data.activeHrs.time);
        }).catch((err: any) => {
            toasterHandler("bg-danger", DisplayErrorMessages(err));
            console.log(err, '-------------err');
        })
    }, [toasterHandler, getDateTimeFormat, getTodayDay, locationId]);

    const getLocationLogo = React.useCallback(() => {
        PostApiMethod('tenant/callHandOff/get-tenant-logo', { locationId: locationId }).then((res: any) => {
            setTenantLogo(res.data.data.logo);
        }).catch((err) => {
            toasterHandler("bg-danger", DisplayErrorMessages(err));
        });
    }, [toasterHandler]);

    const getAgentData = React.useCallback(() => {
        GetApiMethod('tenant/callHandOff/agent-data', {}).then((res: any) => {
            let final_data = res.data.data.filter((item: any) => (item.tenantID === tenantID && (item.status === "online" || item.status === "busy")));
            setOnlineAgentsCount(final_data?.length);
        }).catch((err) => {
            toasterHandler("bg-danger", DisplayErrorMessages(err));
        });
    }, [toasterHandler]);

    const getCallStatusHelper = React.useCallback(() => {
        if (serverValues && serverValues.roomSid) {
            getCallStatus({ roomSid: serverValues.roomSid, locationId: locationId });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locationId, serverValues])


    const terminateQrRoomCall = React.useCallback((showModal: boolean, isEndedByHost = "", terminateNotification: boolean = false) => {
        localStorage.clear();
        setCallRefresh((previousValue) => previousValue + 1);
        if (showModal) {
            setShowPopUp(true);
        } else {
            !checkAbilityToUseZOOM() && setServerValues(undefined);
            removeNotification(locationId ? locationId : "");
            if (terminateVideoCallRef && terminateVideoCallRef.current) {
                terminateVideoCallRef.current();
            }
            let body: any = {
                roomSid: serverValues?.roomSid,
                locationId: locationId
            };
            (!mediaPermissions || terminateNotification || showPopUp) && PostApiMethod('tenant/callHandOff/qr-scan-end-call', body)
                .then((res: any) => {
                    setServerValues(undefined);
                    setRemoteParticipantConnected(false);
                    console.log("QR disconnect successfully");
                }).catch((err) => {
                    console.log("Error : ", err);
                });

            if (isEndedByHost === "ended by host") {
                setServerValues(undefined);
                setRemoteParticipantConnected(false);
            }
        }
        setCallRefresh((previousValue) => previousValue + 1);
    }, [locationId, mediaPermissions, serverValues?.roomSid, showPopUp]);

    const getCallStatus = React.useCallback(async (payload: { roomSid: string, locationId: string, initialCheck?: boolean }) => {
        setShowLoader(true);
        PostApiMethod("tenant/callHandOff/qr-scan-check-call-status", payload).then((callStatusResponse: any) => {
            if (payload.initialCheck) {
                const localQrToken = localStorage.getItem("qrRoomToken")!;
                const localRoomSid = localStorage.getItem("roomSid")!;
                dispatch(setHideNavBar(true));
                setServerValues({ qrRoomToken: localQrToken, roomSid: localRoomSid, sessionName: "", userName: "" });
                setTimeout(() => {
                    getCallStatusHelper()
                }, 120000);
            }
            setShowLoader(false);
        }).catch((error) => {
            toasterHandler("bg-danger", "All our agents are busy right now, so please try again later");
            setShowLoader(false);
            terminateQrRoomCall(false);
        });
    }, [dispatch, getCallStatusHelper, terminateQrRoomCall, toasterHandler]);

    React.useEffect(() => {
        if (remoteParticipantConnected || (!serverValues)) clearInterval(checkCallQueueInterval);
    }, [checkCallQueueInterval, remoteParticipantConnected, serverValues]);

    const updateNotificationQueueLength = React.useCallback((result: any) => {
        PostApiMethod('tenant/callHandOff/getCallQueueData', {}).then((res: any) => {
            console.log(res.data, 'queue data result ---------------------------->');
            let queuedNotifStack = res.data?.QueuedCallLength?.keys?.length > 0 && JSON.parse(res.data?.QueuedCallLength?.keys);
            if (queuedNotifStack?.length > 0) {
                console.log(queuedNotifStack?.indexOf(result?.data?.data?.report_id), 'index of the current call in queue');
                setCallWaitingNumber((queuedNotifStack?.indexOf(result?.data?.data?.report_id)) + 1);
            }
        }).catch((error) => {
            setShowLoader(false);
            terminateQrRoomCall(false);
            toasterHandler("bg-danger", DisplayErrorMessages(error));
        });
    }, [terminateQrRoomCall, toasterHandler]);

    const getAccessToken = React.useCallback(async (roomName: any, locationId: any) => {
        setShowLoader(true);
        let checkForMediaPermission = await getUserMediaDetails();
        if (checkForMediaPermission) {
            PostApiMethod('tenant/callHandOff/get-access-token', { roomName: roomName, locationId: locationId }).then((result: any) => {
                const getAccessResponse = result?.data?.data;
                localStorage.setItem("roomSid", getAccessResponse.roomSid);
                localStorage.setItem("qrRoomToken", getAccessResponse.qrRoomToken);
                localStorage.setItem("sessionName", getAccessResponse.sessionName);
                localStorage.setItem("userName", getAccessResponse.userName);
                setServerValues({ qrRoomToken: getAccessResponse.qrRoomToken, roomSid: getAccessResponse.roomSid, sessionName: getAccessResponse.sessionName, userName: getAccessResponse.userName });
                setShowLoader(false);
                // Triggering the notification for agent
                PostApiMethod("tenant/callHandOff/trigger_notification", { roomName: roomName, locationId: locationId, qrRoomToken: getAccessResponse.qrRoomToken, roomSid: getAccessResponse.roomSid, callCategory: typeOfCall }).then((result: any) => {
                    updateNotificationQueueLength(result);
                    let intervals = setInterval(() => {
                        setCheckCallQueueInterval(intervals);
                        updateNotificationQueueLength(result);
                    }, 60000);
                    return () => {
                        clearInterval(intervals);
                    };
                }).catch((error) => {
                    setShowLoader(false);
                    terminateQrRoomCall(false);
                    toasterHandler("bg-danger", DisplayErrorMessages(error));
                });
                // checking the room status for connected agent after 2 mins
                setTimeout(() => {
                    getCallStatusHelper();
                }, 120000);
            }).catch((error) => {
                setShowLoader(false);
                toasterHandler("bg-danger", DisplayErrorMessages(error));
            });
        } else {
            setShowLoader(false);
            toasterHandler("bg-danger", "Please check the Camera/Microphone permission. Try again after refreshing the page");
        }
    }, [getCallStatusHelper, terminateQrRoomCall, toasterHandler, typeOfCall, updateNotificationQueueLength, mediaPermissions]);

    const getUserMediaDetails = React.useCallback(async () => {
        try {
            // Checking the camera and  microphone permission.
            const constraints = { audio: true, video: true };
            await navigator.mediaDevices.getUserMedia(constraints).then((mediaStream) => {
                mediaStream.getTracks().forEach(function (track) {
                    track.stop();
                });
            });
            return true;
        } catch (error) {
            if (error instanceof DOMException) {
                if (error.name === 'NotAllowedError' || error.name === 'NotFoundError' || error.name === 'NotReadableError') {
                    toasterHandler("bg-danger", `Please check the Camera/Microphone permission. Try again after refreshing the page`);
                    setMediaPermissions(false);
                    return false;
                }
            }
        }
    }, []);


    return (
        <>
            {
                showPopUp &&
                (
                    <AppModal
                        title={"End call?"}
                        bodyText={"Are you sure you want to end the call?"}
                        primaryButtonLabel={"Yes"}
                        secondaryButtonLabel={"No"}
                        primaryButtonEventEmitter={() => { terminateQrRoomCall(false); setShowPopUp(false) }
                        }
                        secondaryButtonEventEmitter={() => setShowPopUp(false)}
                    />
                )
            }
            {
                (serverValues?.qrRoomToken) &&
                (
                    <div className="">
                        {!remoteParticipantConnected && callWaitingNumber >= 1 && (
                            <h3 className="text-danger alert-info">
                                <i className="bi bi-exclamation-triangle-fill"></i>
                                <br />
                                All agents are currently assisting other customers. You are currently number {callWaitingNumber} in the line.
                            </h3>
                        )}
                        {checkAbilityToUseZOOM() ?
                            <ZoomVideoRoom
                                roomName={roomName}
                                roomSid={serverValues?.roomSid}
                                agentId={""}
                                twilioToken={serverValues?.qrRoomToken}
                                locationId={locationId ? locationId : ""}
                                notificationId={""}
                                isKiosk={false}
                                setLocalParticipantInfo={() => { }}
                                terminateVideoCall={() => { }}
                                isHandOffRoom={false}
                                isQrTenantRoom={true}
                                terminateHandOffCall={() => { }}
                                terminateQrRoomCall={terminateQrRoomCall}
                                terminateVideoCallRef={terminateVideoCallRef}
                                addParticipantVideoCall={() => { }}
                                remoteParticipantConnectedFun={() => { setRemoteParticipantConnected(true) }}
                                sessionName={serverValues?.sessionName}
                                userName={serverValues?.userName}
                            />
                            :
                            <VideoRoom
                                roomName={roomName}
                                roomSid={serverValues?.roomSid}
                                agentId={""}
                                twilioToken={serverValues?.qrRoomToken}
                                locationId={locationId ? locationId : ""}
                                notificationId={""}
                                isKiosk={false}
                                setLocalParticipantInfo={() => { }}
                                terminateVideoCall={() => { }}
                                isHandOffRoom={false}
                                isQrTenantRoom={true}
                                terminateHandOffCall={() => { }}
                                terminateQrRoomCall={terminateQrRoomCall}
                                terminateVideoCallRef={terminateVideoCallRef}
                                addParticipantVideoCall={() => { }}
                                remoteParticipantConnectedFun={() => { setRemoteParticipantConnected(true) }}
                                sessionName={""}
                                userName={""}
                            />
                        }

                    </div>
                )
            }
            {
                (!serverValues && !showLoader) &&
                (

                    <div className="qr_room">
                        <div className='qr_room_inner'>
                            {(tenantLogo) ? <img className="tenant_logo" src={tenantLogo} alt="RCM Logo"></img> : <></>}

                            <Button disabled={!mediaPermissions || isHoliday || inBreakHrs || onlineAgentsCount === 0} onClick={() => { getAccessToken(roomName, locationId) }}><i className="bi bi-telephone-outbound-fill me-2 shake-icon"></i>Join Call</Button>
                        </div >

                        {
                            isHoliday || inBreakHrs ? (
                                <span className="text-center text-danger fs-3 mt-4 p-3">
                                    <b>
                                        The office is currently closed.<br />Please check back during normal business hours.
                                        {inBreakHrs && currentDayWorkHrs ? (
                                            <>
                                                <br />
                                                Office Hours: {currentDayWorkHrs}
                                            </>
                                        ) : null}
                                    </b>
                                </span>
                            ) : (
                                onlineAgentsCount === 0 ? (
                                    customerContact ? (
                                        <span className="text-center text-danger fs-4 mt-4">
                                            <b>
                                                We are sorry, no agents are online to complete your call.<br />Please call{" "}
                                                {PhoneNumberFormatter(customerContact?.toString().slice(1), customerContact?.toString().slice(1).length)}{" "}
                                                for further assistance.
                                            </b>
                                        </span>
                                    ) : (
                                        <span className="text-center text-danger fs-4 mt-4">
                                            <b>We are sorry, but no agents are available at this time.<br />Please try again later.</b>
                                        </span>
                                    )
                                ) : null
                            )
                        }
                    </div >
                )
            }
            {
                (showLoader) && (<AppLoader />)
            }
        </>
    )
}

export default QrRoom;
